import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

/**
 * Determines what records are implicitly covered by a reference
 *
 * | Inheritance | Description |
 * | :-- | :-- |
 * | `=` _None_ | A record is only covered by explicit references to it. Usually, this is overly strict, but is generally a safe default behavior |
 * | `+` _Ancestors_ | A record is covered by references to it or any of its ancestors. E.g., if an employee oversees a department, they implicitly oversee all subdepartments in that department |
 * | `-` _Descendants_ | A record is covered by references to it or any of its descendants. E.g., if an employee works for a department, they implicitly work for the division containing that department |
 */
export type Inheritance = '=' | '+' | '-'
export namespace Inheritance {
  export const ALL: readonly Inheritance[] = ['=', '+', '-']
  export const isInheritance = TypeChecker.Factory.oneOf(ALL).usingAlias(
    'dms.query.Inheritance',
  )
  export const assertIsInheritance: TypeAsserter<Inheritance> =
    TypeAsserter.Factory.from(isInheritance)

  export type IncludesAncestors = Satisfies<'+', Inheritance>
  export function includesAncestors<T extends Inheritance | undefined>(
    inheritance: T,
  ): inheritance is Extract<T, IncludesAncestors> {
    return inheritance === '+'
  }

  export type IncludesDescendants = Satisfies<'-', Inheritance>
  export function includesDescendants<T extends Inheritance | undefined>(
    inheritance: T,
  ): inheritance is Extract<T, IncludesDescendants> {
    return inheritance === '-'
  }
}
